import { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import {
  Search as SearchIcon,
  ErrorOutline,
  WarningAmber,
  TableChartOutlined,
  RemoveCircleOutline as ExcludeIcon,
  RestoreFromTrash as RestoreIcon,
} from "@mui/icons-material";
import MUIButton from "@/components/ui/Button";
import { UIComponentSize, ButtonStyleType } from "@/types/shared/uiComponents";
import { useAppTheme } from "@/hooks/useAppTheme";
import { CompactTable, type CompactTableColumn } from "@/components/common/CompactTable";
import { SectionCard } from "@/components/common/SectionCard";
import { HintIcon } from "@/components/ui/mui/HintIcon";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import {
  TargetSchema,
  type BOBClaimantForecast,
  type BOBResolutionStatus,
  type ClientReviewGroup,
  type DocumentRead,
  ResourceName,
  type MatchStatus,
} from "@/types";
import { useGetOne, useRedirect } from "react-admin";
import { EntityLink } from "../EntityLink";
import { ReviewActions } from "./ReviewActions";
import { RejectDocumentDialog } from "./RejectDocumentDialog";
import { DeleteImpactDialog } from "../DeleteImpactDialog";
import { useCascadeDeletePermission } from "../../hooks/useCascadeDeletePermission";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { usePartner } from "@/providers/partner/PartnerProvider";
import { RowDecisionsNotice, selectRowDecisions } from "./RowDecisionsNotice";
import { SourcePreviewDrawer } from "./SourcePreviewDrawer";
import { FieldMappingDialog } from "@/resources/documents/components/field-mapping/FieldMappingDialog";
import { isSpreadsheetDocument, isPdfDocument } from "../../utils/documentFormat";
import { useMultiClientReviewProjection } from "../../hooks/useMultiClientReviewProjection";
import { useDocumentTransformationData } from "../../hooks/useDocumentTransformationData";
import { CompletenessCard, CompletenessChip, hasCompletenessSignal } from "./Completeness";
import { useSourcePreview } from "../../hooks/useSourcePreview";
import { useTransformationRecords } from "../../hooks/useTransformationRecords";
import { useReviewApproval } from "../../hooks/useReviewApproval";

interface MultiClientReviewPanelProps {
  documentId: string | undefined;
  enabled: boolean;
}

const pluralize = (count: number, singular: string): string => (count === 1 ? singular : `${singular}s`);

/** Thousands-separated count (e.g. 24,021). Dollar amounts use formatUSD. */
const formatCount = (n: number): string => n.toLocaleString();

const EMPTY_EXCLUDED: ReadonlySet<number> = new Set<number>();

/** All extracted_record_index values under a claimant / client (cascade + tally scope). */
const claimantRowIndices = (claimant: BOBClaimantForecast): number[] =>
  claimant.rows.map((r) => r.extracted_record_index);
const clientRowIndices = (group: ClientReviewGroup): number[] => group.claimants.flatMap((c) => claimantRowIndices(c));

/** True when the entity has rows and every one is excluded — drives group-row muting. */
const allExcluded = (indices: number[], excluded: ReadonlySet<number>): boolean =>
  indices.length > 0 && indices.every((i) => excluded.has(i));

/**
 * Per-claimant row tallies derived from the claimant's rows. `excluded` rows are
 * skipped so every number reflects what will actually load (#5592). `rowCount` is
 * the total rows (for the all-excluded empty-state); `total` is the included count.
 */
const claimantTotals = (claimant: BOBClaimantForecast, excluded: ReadonlySet<number> = EMPTY_EXCLUDED) => {
  let requested = 0;
  let reimbursed = 0;
  let unlinked = 0;
  let review = 0;
  let total = 0;
  for (const row of claimant.rows) {
    if (excluded.has(row.extracted_record_index)) continue;
    total++;
    requested += row.requested_amount || 0;
    reimbursed += row.reimbursement_amount || 0;
    // resolution_status is typed non-null but crosses the wire from a
    // hand-authored backend type; guard against a missing value so a single
    // bad row can't throw here (this runs in render paths) and blank the panel.
    const status = row.resolution_status ?? "";
    if (status === "resolved_unlinked") unlinked++;
    else if (status.startsWith("quarantine")) review++;
  }
  return { requested, reimbursed, unlinked, review, total, rowCount: claimant.rows.length };
};

/**
 * Per-client row tallies (resolved / unlinked / review counts, reimbursed total,
 * and claimants that still have ≥1 included row), recomputed from the group's rows
 * minus `excluded` so the client row previews the post-exclusion load (#5592). With
 * no exclusions these equal the backend's pre-aggregated group counts.
 */
const clientTotals = (group: ClientReviewGroup, excluded: ReadonlySet<number> = EMPTY_EXCLUDED) => {
  let resolved = 0;
  let unlinked = 0;
  let review = 0;
  let reimbursed = 0;
  let claimants = 0;
  for (const cl of group.claimants) {
    let clHasIncluded = false;
    for (const row of cl.rows) {
      if (excluded.has(row.extracted_record_index)) continue;
      clHasIncluded = true;
      reimbursed += row.reimbursement_amount || 0;
      const status = row.resolution_status ?? "";
      if (status === "resolved") resolved++;
      else if (status === "resolved_unlinked") unlinked++;
      else review++;
    }
    if (clHasIncluded) claimants++;
  }
  return { resolved, unlinked, review, reimbursed, claimants };
};

// Matches ReimbursementRowStateChip's vocabulary so the multi-client review reads
// the same as the single-client reimbursement review.
const baseChipSx = {
  height: 20,
  "& .MuiChip-label": { px: 0.75, fontSize: "0.7rem", fontWeight: 600 },
};

/** Client resolution chip: existing (matched) / new (will create) / unresolved. */
const ClientMatchChip = ({ status }: { status: ClientReviewGroup["match_status"] }) => {
  const { theme } = useAppTheme();
  if (status === "matched") {
    return (
      <Tooltip
        title="Resolved to an existing client. Approving loads this client's rows onto it."
        placement="top"
        arrow
      >
        <Chip
          label="Existing client"
          size="small"
          sx={{ ...baseChipSx, bgcolor: alpha(theme.palette.success.main, 0.15), color: "success.dark" }}
        />
      </Tooltip>
    );
  }
  if (status === "new") {
    return (
      <Tooltip
        title="No existing client matched this name. Approving creates a new client in this partner."
        placement="top"
        arrow
      >
        <Chip
          label="New client"
          size="small"
          sx={{ ...baseChipSx, bgcolor: alpha(theme.palette.info.main, 0.15), color: "info.dark" }}
        />
      </Tooltip>
    );
  }
  return (
    <Tooltip
      title="No client/group name on these rows — they cannot resolve a client and need review."
      placement="top"
      arrow
    >
      <Chip
        label="Unresolved"
        size="small"
        sx={{ ...baseChipSx, bgcolor: alpha(theme.palette.error.main, 0.15), color: "error.dark" }}
      />
    </Tooltip>
  );
};

/**
 * Claimant "What happens" cell — the BOB analogue of ReimbursementForecastChip.
 * Same two-chip structure (status chip + action chip) and styling, but with
 * client/policy-resolution semantics: "unlinked" means no POLICY (not no
 * stoploss claim), and "review" means the row quarantined (no/ambiguous client
 * or multiple policy terms). Status chip is omitted for a plain match to keep
 * the common case quiet, exactly like the single-client chip.
 */
const ClaimantStatusChip = ({ status }: { status: MatchStatus }) => {
  const { theme } = useAppTheme();
  if (status === "new") {
    return (
      <Tooltip title="New claimant — approving creates the claimant record under this client." placement="top" arrow>
        <Chip
          label="New claimant"
          size="small"
          sx={{ ...baseChipSx, bgcolor: alpha(theme.palette.success.main, 0.15), color: "success.dark" }}
        />
      </Tooltip>
    );
  }
  if (status === "matched_backfill") {
    return (
      <Tooltip
        title="Matched an existing claimant; approving also writes the incoming Member ID."
        placement="top"
        arrow
      >
        <Chip
          label="Adds Member ID"
          size="small"
          sx={{ ...baseChipSx, bgcolor: alpha(theme.palette.info.main, 0.12), color: "info.dark" }}
        />
      </Tooltip>
    );
  }
  return null; // plain match: no status chip (matches single-client behavior)
};

const ClaimantForecastChip = ({
  claimant,
  excludedIndices = EMPTY_EXCLUDED,
}: {
  claimant: BOBClaimantForecast;
  excludedIndices?: ReadonlySet<number>;
}) => {
  const { theme } = useAppTheme();
  const { total, unlinked, review, requested, reimbursed, rowCount } = claimantTotals(claimant, excludedIndices);

  let action: { label: string; sx: object; tip: string };
  if (total === 0) {
    // Distinguish "no rows at all" from "every row excluded" — the latter is a
    // reviewer choice, not an extraction gap.
    action =
      rowCount > 0
        ? {
            label: "Excluded",
            sx: { borderColor: "divider", color: "text.disabled" },
            tip: `All ${pluralize(rowCount, "row")} for ${claimant.claimant_name} are excluded — nothing loads for this claimant.`,
          }
        : {
            label: "No rows",
            sx: { borderColor: "divider", color: "text.disabled" },
            tip: "No reimbursement rows for this claimant.",
          };
  } else {
    const money = `${formatUSD(requested)} requested → ${formatUSD(reimbursed)} reimbursed`;
    if (review > 0) {
      action = {
        label: `Creates ${formatCount(total)} · ${formatCount(review)} review`,
        sx: { bgcolor: alpha(theme.palette.error.main, 0.15), color: "error.dark" },
        tip: `Approving creates ${pluralize(total, "reimbursement")} for ${claimant.claimant_name} (${money}). ${review} ${pluralize(review, "row")} need review before they resolve a client/policy.`,
      };
    } else if (unlinked > 0) {
      action = {
        label: `Creates ${formatCount(total)} · ${formatCount(unlinked)} no policy`,
        sx: { bgcolor: alpha(theme.palette.warning.main, 0.1), color: "warning.dark" },
        tip: `Approving creates ${pluralize(total, "reimbursement")} for ${claimant.claimant_name} (${money}). ${unlinked} have no policy term covering their date and load with no policy attached.`,
      };
    } else {
      action = {
        label: `Creates ${formatCount(total)}`,
        sx: { bgcolor: alpha(theme.palette.info.main, 0.15), color: "info.dark" },
        tip: `Approving creates ${pluralize(total, "reimbursement")} for ${claimant.claimant_name} (${money}), each attached to the resolved policy.`,
      };
    }
  }

  const variant = total === 0 ? "outlined" : undefined;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
      <ClaimantStatusChip status={claimant.match_status} />
      <Tooltip title={action.tip} placement="top" arrow>
        <Chip label={action.label} size="small" variant={variant} sx={{ ...baseChipSx, ...action.sx }} />
      </Tooltip>
    </Box>
  );
};

/** Per-row resolution chip (client/policy resolution — the BOB analogue of ReimbursementRowStateChip). */
const RowResolutionChip = ({ status, reason }: { status: BOBResolutionStatus; reason?: string }) => {
  const { theme } = useAppTheme();
  const cfg: Record<BOBResolutionStatus, { label: string; sx: object; tip: string }> = {
    resolved: {
      label: "Resolved",
      sx: { bgcolor: alpha(theme.palette.info.main, 0.15), color: "info.dark" },
      tip: "Client and policy resolved. Approving loads this row attached to the policy.",
    },
    resolved_unlinked: {
      label: "No policy",
      sx: { bgcolor: alpha(theme.palette.warning.main, 0.1), color: "warning.dark" },
      tip: "Client resolved, but no policy term covers the row's date. Approving loads it with no policy attached.",
    },
    quarantine_no_client: {
      label: "Needs review",
      sx: { bgcolor: alpha(theme.palette.error.main, 0.15), color: "error.dark" },
      tip: "No client/group name on this row.",
    },
    quarantine_no_claimant: {
      label: "No claimant",
      sx: { bgcolor: alpha(theme.palette.error.main, 0.15), color: "error.dark" },
      tip: "No claimant on this row — it can't load (claimant is required) and creates no client.",
    },
    quarantine_ambiguous_client: {
      label: "Needs review",
      sx: { bgcolor: alpha(theme.palette.error.main, 0.15), color: "error.dark" },
      tip: "Client name matched more than one client.",
    },
    quarantine_multi_policy: {
      label: "Needs review",
      sx: { bgcolor: alpha(theme.palette.error.main, 0.15), color: "error.dark" },
      tip: "Client + effective date matched more than one policy term.",
    },
  };
  const entry = cfg[status] ?? cfg.quarantine_no_client;
  return (
    <Tooltip title={reason || entry.tip} placement="top" arrow>
      <Chip label={entry.label} size="small" sx={{ ...baseChipSx, ...entry.sx }} />
    </Tooltip>
  );
};

/**
 * Compact "40 resolved · 2 no policy · 1 review" cell for the client row.
 * Counts reflect what will load — excluded rows are subtracted (#5592). Shows an
 * explicit "all excluded" note when the reviewer has dropped every row.
 */
const RowCountsCell = ({
  group,
  excludedIndices = EMPTY_EXCLUDED,
}: {
  group: ClientReviewGroup;
  excludedIndices?: ReadonlySet<number>;
}) => {
  const { resolved, unlinked, review } = clientTotals(group, excludedIndices);
  if (resolved + unlinked + review === 0 && allExcluded(clientRowIndices(group), excludedIndices)) {
    return (
      <Typography variant="caption" color="text.disabled" fontWeight={600}>
        all excluded
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {resolved > 0 && (
        <Typography variant="caption" color="info.dark" fontWeight={600}>
          {formatCount(resolved)} resolved
        </Typography>
      )}
      {unlinked > 0 && (
        <Typography variant="caption" color="warning.dark" fontWeight={600}>
          {formatCount(unlinked)} no policy
        </Typography>
      )}
      {review > 0 && (
        <Typography variant="caption" color="error.dark" fontWeight={600}>
          {formatCount(review)} review
        </Typography>
      )}
    </Stack>
  );
};

type RowEntry = { id: string; row: BOBClaimantForecast["rows"][number]; clientId?: string };
type ClaimantEntry = { id: string; claimant: BOBClaimantForecast };
type ClientEntry = { id: string; group: ClientReviewGroup };

/**
 * The concrete next step for a row that didn't cleanly resolve, so the reviewer
 * isn't left at a dead-end "Needs review" chip. `linkClient` says whether opening
 * the resolved client is part of the fix (false when there's no single client to
 * open — ambiguous or missing). `detail` is the longer "why" for the HintIcon.
 */
const rowGuidance = (status: BOBResolutionStatus): { text: string; linkClient: boolean; detail: string } | null => {
  switch (status) {
    case "resolved":
      return null; // nothing to do
    case "resolved_unlinked":
      return {
        text: "No policy covers this date — add a covering policy term.",
        linkClient: true,
        detail:
          "The client resolved, but no policy term covers this row's effective date. It will load with no policy attached. To attach a policy, add/extend a policy term on the client that covers the date, then re-process the document.",
      };
    case "quarantine_multi_policy":
      return {
        text: "Fix overlapping policy terms, then re-process.",
        linkClient: true,
        detail:
          "The client + effective date matched more than one policy term. Correct the overlapping terms on the client's policies so exactly one covers the date, then re-process the document.",
      };
    case "quarantine_ambiguous_client":
      return {
        text: "Group name matches multiple clients — disambiguate, then re-process.",
        linkClient: false,
        detail:
          "This group name matched more than one client in the partner. Disambiguate (rename/merge the duplicate clients) so the name resolves to one, then re-process the document.",
      };
    case "quarantine_no_client":
      return {
        text: "No client/group name on this row — exclude it.",
        linkClient: false,
        detail: "This row carries no client/group name, so it can't resolve a client. Exclude it from the load.",
      };
    default:
      return null;
  }
};

/** "What to do" cell — visible next step + (where applicable) a link to the client to fix. */
const RowWhatToDoCell = ({ row, clientId }: { row: RowEntry["row"]; clientId?: string }) => {
  const guidance = rowGuidance(row.resolution_status);
  if (!guidance) {
    return (
      <Typography variant="caption" color="text.disabled">
        —
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Typography variant="caption" color="text.secondary">
        {guidance.text}
      </Typography>
      <HintIcon hint={guidance.detail} variant="info" size="xs" />
      {guidance.linkClient && clientId && <EntityLink entityId={clientId} targetSchema={TargetSchema.CLIENT} />}
    </Stack>
  );
};

// Level 3: a claimant's reimbursement rows.
// Text-bearing cells are wrapped in <Typography> so the excluded-row muting
// (getRowSx below) can strike them via `& .MuiTypography-root`, matching the
// single-client RecordReviewGrid treatment; chips are deliberately left
// un-struck.
const reimbursementRowColumns: CompactTableColumn<RowEntry>[] = [
  {
    key: "row",
    label: "Row",
    width: "60px",
    render: (r) => <Typography variant="body2">#{r.row.extracted_record_index + 1}</Typography>,
  },
  {
    key: "status",
    label: "Status",
    width: "130px",
    render: (r) => <RowResolutionChip status={r.row.resolution_status} reason={r.row.resolution_reason} />,
  },
  {
    key: "what_to_do",
    label: "What to do",
    width: "2fr",
    render: (r) => <RowWhatToDoCell row={r.row} clientId={r.clientId} />,
  },
  {
    key: "requested",
    label: "Requested",
    width: "110px",
    align: "right",
    render: (r) => <Typography variant="body2">{formatUSD(r.row.requested_amount)}</Typography>,
  },
  {
    key: "reimbursed",
    label: "Reimbursed",
    width: "110px",
    align: "right",
    render: (r) => <Typography variant="body2">{formatUSD(r.row.reimbursement_amount)}</Typography>,
  },
];

/** Row muting for reviewer-excluded rows — mirrors single-client RecordReviewGrid. */
const excludedRowSx = {
  opacity: 0.55,
  bgcolor: "action.disabledBackground",
  "& .MuiTypography-root": { textDecoration: "line-through" },
};

/**
 * Shared CompactTable selection toolbar — offers Exclude while any selected item
 * is still included, otherwise Restore. Reused at all three levels (client,
 * claimant, row) so exclude/restore reads identically wherever you select (#5592).
 */
const ExcludeRestoreToolbar = ({
  selectedCount,
  someIncluded,
  onExclude,
  onRestore,
}: {
  selectedCount: number;
  someIncluded: boolean;
  onExclude: () => void;
  onRestore: () => void;
}) =>
  selectedCount > 0 ? (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 2,
        py: 1,
        bgcolor: "action.selected",
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <Typography variant="body2" fontWeight={500}>
        {selectedCount} selected
      </Typography>
      <Box sx={{ display: "flex", gap: 1 }}>
        {someIncluded ? (
          <Button size="small" startIcon={<ExcludeIcon fontSize="small" />} onClick={onExclude}>
            Exclude
          </Button>
        ) : (
          <Button size="small" color="success" startIcon={<RestoreIcon fontSize="small" />} onClick={onRestore}>
            Restore
          </Button>
        )}
      </Box>
    </Box>
  ) : null;

/**
 * Selection + cascade-exclude wiring for one CompactTable level. `indicesOf`
 * maps a selected entry to the extracted_record_index values it owns — one row at
 * level 3, all of a claimant's rows at level 2, all of a client's rows at level 1
 * — so Exclude/Restore cascades down to every underlying reimbursement row.
 */
function useExcludeSelection<T extends { id: string }>(
  data: T[],
  indicesOf: (entry: T) => number[],
  excludedIndices: ReadonlySet<number>,
  onExclude: (indices: number[]) => void,
  onRestore: (indices: number[]) => void
) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const selectedIndices = useMemo(() => {
    const byId = new Map(data.map((d) => [d.id, d]));
    return selectedIds.flatMap((id) => {
      const entry = byId.get(id);
      return entry ? indicesOf(entry) : [];
    });
  }, [selectedIds, data, indicesOf]);
  const someIncluded = selectedIndices.some((i) => !excludedIndices.has(i));
  const toolbar = (
    <ExcludeRestoreToolbar
      selectedCount={selectedIds.length}
      someIncluded={someIncluded}
      onExclude={() => {
        onExclude(selectedIndices);
        setSelectedIds([]);
      }}
      onRestore={() => {
        onRestore(selectedIndices);
        setSelectedIds([]);
      }}
    />
  );
  return { selectedIds, setSelectedIds, toolbar };
}

/**
 * Level-3 reimbursement rows for one claimant, with per-record exclude/restore
 * (#5592). Selection + batch toolbar mirror the single-client RecordReviewGrid;
 * excluded indices lift to the panel so they flow into the accept payload's
 * `excluded_record_indices`. Each row's `extracted_record_index` is the 0-based
 * position the worker excludes on (bob_resolution `BOBRow{Index: i}` →
 * document_loading workflow `excludedSet[i]`), so no remapping is needed.
 */
const rowIndicesOf = (r: RowEntry): number[] => [r.row.extracted_record_index];

const ClaimantRowsTable = ({
  claimant,
  clientId,
  excludedIndices,
  onExclude,
  onRestore,
}: {
  claimant: BOBClaimantForecast;
  clientId?: string;
  excludedIndices: ReadonlySet<number>;
  onExclude: (indices: number[]) => void;
  onRestore: (indices: number[]) => void;
}) => {
  const data = useMemo<RowEntry[]>(
    () => claimant.rows.map((row) => ({ id: String(row.extracted_record_index), row, clientId })),
    [claimant.rows, clientId]
  );
  const { selectedIds, setSelectedIds, toolbar } = useExcludeSelection(
    data,
    rowIndicesOf,
    excludedIndices,
    onExclude,
    onRestore
  );

  return (
    <CompactTable<RowEntry>
      data={data}
      columns={reimbursementRowColumns}
      rowsPerPage={10}
      fitContent
      emptyMessage="No rows."
      selectable
      selectedIds={selectedIds}
      onSelectionChange={setSelectedIds}
      selectionToolbar={toolbar}
      getRowSx={(r) => (excludedIndices.has(r.row.extracted_record_index) ? excludedRowSx : {})}
    />
  );
};

// Level 2: a client's claimants (each expands to its rows). Columns are built
// per-render so the Rows / Requested / Reimbursed / "What happens" cells reflect
// exclusions (#5592) — with no exclusions they equal the raw projection.
const buildClaimantColumns = (excludedIndices: ReadonlySet<number>): CompactTableColumn<ClaimantEntry>[] => [
  {
    key: "claimant",
    label: "Claimant",
    width: "1.5fr",
    render: (c) => <Typography variant="body2">{c.claimant.claimant_name || "(unnamed claimant)"}</Typography>,
  },
  {
    key: "what_happens",
    label: "What Happens",
    width: "1.5fr",
    render: (c) => <ClaimantForecastChip claimant={c.claimant} excludedIndices={excludedIndices} />,
  },
  {
    key: "rows",
    label: "Rows",
    width: "70px",
    align: "center",
    render: (c) => (
      <Typography variant="body2">{formatCount(claimantTotals(c.claimant, excludedIndices).total)}</Typography>
    ),
  },
  {
    key: "requested",
    label: "Requested",
    width: "120px",
    align: "right",
    render: (c) => (
      <Typography variant="body2">{formatUSD(claimantTotals(c.claimant, excludedIndices).requested)}</Typography>
    ),
  },
  {
    key: "reimbursed",
    label: "Reimbursed",
    width: "120px",
    align: "right",
    render: (c) => (
      <Typography variant="body2">{formatUSD(claimantTotals(c.claimant, excludedIndices).reimbursed)}</Typography>
    ),
  },
];

const claimantIndicesOf = (c: ClaimantEntry): number[] => claimantRowIndices(c.claimant);
const clientIndicesOf = (c: ClientEntry): number[] => clientRowIndices(c.group);

const ClientClaimantsTable = ({
  group,
  excludedIndices,
  onExclude,
  onRestore,
}: {
  group: ClientReviewGroup;
  excludedIndices: ReadonlySet<number>;
  onExclude: (indices: number[]) => void;
  onRestore: (indices: number[]) => void;
}) => {
  const clientId = group.existing_client_id ?? undefined;
  const data = useMemo<ClaimantEntry[]>(
    () =>
      group.claimants.map((claimant, i) => ({
        id: `${claimant.existing_claimant_id ?? claimant.claimant_name}-${i}`,
        claimant,
      })),
    [group.claimants]
  );
  const columns = useMemo(() => buildClaimantColumns(excludedIndices), [excludedIndices]);
  const { selectedIds, setSelectedIds, toolbar } = useExcludeSelection(
    data,
    claimantIndicesOf,
    excludedIndices,
    onExclude,
    onRestore
  );

  return (
    <CompactTable<ClaimantEntry>
      data={data}
      columns={columns}
      rowsPerPage={10}
      fitContent
      emptyMessage="No claimants resolved for this client."
      selectable
      selectedIds={selectedIds}
      onSelectionChange={setSelectedIds}
      selectionToolbar={toolbar}
      getRowSx={(c) => (allExcluded(claimantRowIndices(c.claimant), excludedIndices) ? excludedRowSx : {})}
      expandRow={(c) => (
        <Box sx={{ pl: 2, py: 1 }}>
          <ClaimantRowsTable
            claimant={c.claimant}
            clientId={clientId}
            excludedIndices={excludedIndices}
            onExclude={onExclude}
            onRestore={onRestore}
          />
        </Box>
      )}
    />
  );
};

/**
 * Lazily loads the transformation + template ONLY when Field Mapping is opened,
 * so the heavy extracted_records fetch doesn't run on every BOB review mount
 * (the panel itself renders from the pre-aggregated projection). clientId is ""
 * — a multi-client document has no single bound client, and field mapping is
 * column → target-field, which is client-agnostic.
 */
const BOBFieldMappingDialog = ({ documentId, onClose }: { documentId: string; onClose: () => void }) => {
  const { latestTransformation, template, isLoading } = useDocumentTransformationData({ documentId });
  const { data: doc } = useGetOne<DocumentRead>("documents", { id: documentId }, { enabled: !!documentId });

  // One page, for the critical-field advisory's conditional entries only
  // (#6755). The advisory asks questions like "do any records carry a
  // denial_amount" — a sample answers those, and the transformation row no
  // longer carries the record set. Declared before the early return below to
  // keep hook order stable.
  const { records: advisoryRecords } = useTransformationRecords({
    documentId,
    transformationId: latestTransformation?.id,
    enabled: !!latestTransformation?.id,
    autoLoadUpTo: 0,
  });

  if (isLoading || !latestTransformation) return null;
  return (
    <FieldMappingDialog
      open
      onClose={onClose}
      transformation={latestTransformation}
      transformationTemplates={template ? [template] : []}
      clientId=""
      documentId={documentId}
      isSpreadsheet={isSpreadsheetDocument(doc)}
      isPdf={isPdfDocument(doc)}
      // Record-conditional required fields evaluate against the extracted
      // records so the blocking alert doesn't surface them unconditionally.
      // Family-deductible conditions (per-claimant projection) aren't available
      // in the book-of-business panel and are enforced downstream at Approve.
      advisoryContext={{ extractedRecords: advisoryRecords }}
    />
  );
};

type BOBIssueSummary = {
  noClient: number;
  ambiguous: number;
  multiPolicy: number;
  unlinked: number;
  newClients: number;
  noClaimant: number;
};

const hasSidebarIssues = (s: BOBIssueSummary): boolean =>
  s.noClient + s.ambiguous + s.multiPolicy + s.unlinked + s.noClaimant > 0;

/** One issue line — mirrors ReviewStatusAlert's icon + message row. */
const IssueRow = ({ severity, children }: { severity: "error" | "warning"; children: React.ReactNode }) => {
  const Icon = severity === "error" ? ErrorOutline : WarningAmber;
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, pl: 0.5 }}>
      <Icon sx={{ fontSize: 16, color: `${severity}.main`, mt: "2px" }} />
      <Typography variant="body2" color="text.secondary">
        {children}
      </Typography>
    </Box>
  );
};

const SidebarHeading = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="caption"
    sx={{ textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, color: "text.secondary" }}
  >
    {children}
  </Typography>
);

/**
 * BOB review sidebar — the multi-client analogue of ReviewStatusAlert's "Mapping
 * Review" card, using the same SectionCard chrome + icon/row pattern so the issues
 * panel is consistent across review types. "Needs review" lists the quarantine
 * reasons (rows that won't resolve a client/policy); "No policy match" is the
 * advisory channel (rows that load without a policy link). Renders nothing when
 * there are no issues (full-width grid).
 */
const BOBReviewSidebar = ({ summary }: { summary: BOBIssueSummary }) => {
  // "Needs review" = actionable items only. Claimant-less separator rows are NOT
  // here — they just skip (no action), so they live under "Skipped" below.
  const needsReview = summary.noClient + summary.ambiguous + summary.multiPolicy > 0;
  return (
    <SectionCard title="Review Summary" compact>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {needsReview && (
          <>
            <SidebarHeading>Needs review</SidebarHeading>
            {summary.noClient > 0 && (
              <IssueRow severity="error">
                {formatCount(summary.noClient)} row{summary.noClient === 1 ? "" : "s"} — no client/group name
              </IssueRow>
            )}
            {summary.ambiguous > 0 && (
              <IssueRow severity="error">
                {formatCount(summary.ambiguous)} row{summary.ambiguous === 1 ? "" : "s"} — client name matches multiple
                clients
              </IssueRow>
            )}
            {summary.multiPolicy > 0 && (
              <IssueRow severity="error">
                {formatCount(summary.multiPolicy)} row{summary.multiPolicy === 1 ? "" : "s"} — multiple policy terms
                cover the date
              </IssueRow>
            )}
          </>
        )}

        {needsReview && summary.unlinked > 0 && <Divider sx={{ my: 0.5 }} />}

        {summary.unlinked > 0 && (
          <>
            <SidebarHeading>No policy match</SidebarHeading>
            <IssueRow severity="warning">
              {formatCount(summary.unlinked)} row{summary.unlinked === 1 ? "" : "s"} — no policy term covers the date;
              loads with no policy attached
            </IssueRow>
          </>
        )}

        {summary.noClaimant > 0 && (
          <>
            {(needsReview || summary.unlinked > 0) && <Divider sx={{ my: 0.5 }} />}
            <SidebarHeading>Skipped (no action needed)</SidebarHeading>
            <Typography variant="body2" color="text.secondary" sx={{ pl: 0.5 }}>
              {formatCount(summary.noClaimant)} row{summary.noClaimant === 1 ? "" : "s"} — no claimant (blank/separator
              rows); not loaded, no client created.
            </Typography>
          </>
        )}

        {summary.newClients > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, fontStyle: "italic" }}>
            {formatCount(summary.newClients)} new client{summary.newClients === 1 ? "" : "s"} will be created on
            approve.
          </Typography>
        )}
      </Box>
    </SectionCard>
  );
};

/**
 * Lazily loads the transformation + parses the source spreadsheet ONLY when the
 * reviewer opens "View source" — so the heavy fetch/parse stays off the panel's
 * initial mount. Reuses the single-client SourcePreviewDrawer + useSourcePreview
 * so the spreadsheet preview is identical across review types.
 */
// Files over this row count disable "View source" on the BOB surface (the
// per-record row numbers + the downloadable file cover those). See sourceTooLarge.
//
// SpreadsheetPreview now virtualises above VIRTUALISE_ABOVE_ROWS (= this same
// bound), so RENDERING is no longer the limiter — but useSourcePreview still
// downloads and parses the whole workbook in the browser, which is untested at
// BOB register scale, so this gate is deliberately retained until that parse
// path is measured (#6755 left it open). Raising the cap here without that
// measurement just moves the freeze from render to parse.
const SOURCE_PREVIEW_MAX_ROWS = 2000;

const BOBSourcePreview = ({ documentId, onClose }: { documentId: string; onClose: () => void }) => {
  const { latestTransformation, template, hasTransformation } = useDocumentTransformationData({ documentId });

  // Records come from the windowed endpoint now that the transformation row no
  // longer carries them (#6755). Highlight coverage therefore follows loaded
  // pages — the sheet always renders in full, but a row whose record has not
  // been fetched shows no highlight. BOB documents are well under the
  // auto-load bound in practice, so every record is present.
  // hasMoreRecords is taken rather than discarded (#6900 part B). The comment
  // above reasons that BOB documents sit under the auto-load bound "in
  // practice" -- which is an assumption, not a guarantee. Threading it means
  // that if a BOB file ever does page, the preview SAYS its highlights stop
  // short instead of showing unhighlighted rows that read as "nothing was
  // extracted here".
  const { records, hasMore } = useTransformationRecords({
    documentId,
    transformationId: latestTransformation?.id,
    enabled: !!latestTransformation?.id,
  });

  const sp = useSourcePreview({
    documentId,
    transformation: latestTransformation ?? null,
    enabled: true,
    fieldMappings: template?.field_mappings,
    records,
  });

  return (
    <SourcePreviewDrawer
      open
      onClose={onClose}
      sheetData={sp.sheetData}
      highlightsFor={sp.highlightsFor}
      highlightedThroughRow={sp.highlightedThroughRow}
      hasMoreRecords={hasMore}
      sheetName={sp.sheetName}
      headerRowIndex={sp.headerRowIndex}
      isLoading={sp.isLoading}
      error={sp.error}
      focusRow={null}
      templateUnavailable={hasTransformation && !template?.field_mappings?.length}
    />
  );
};

/**
 * Multi-client ("book of business") review surface (#5321 / ADR-057). Built on the
 * same CompactTable + chip vocabulary as the single-client reimbursement review:
 * one row per CLIENT, expand → that client's claimants, expand → each claimant's
 * reimbursement rows. Read-only forecast of what approving will do per client
 * (new vs existing client, new vs matched claimant, resolved / unlinked / review
 * per row, amounts). Replaces the single-client ClientGate + grid for multi-client
 * documents; the single-client review path is unchanged.
 */
export const MultiClientReviewPanel = ({ documentId, enabled }: MultiClientReviewPanelProps) => {
  const { projection, isLoading, error } = useMultiClientReviewProjection({ documentId, enabled });
  // Completeness verdict (#5767) — carrier registers also lose members to
  // extraction, so the guardrail belongs on this surface too. The projection
  // doesn't carry it; read it from the latest transformation's metadata.
  const { latestTransformation } = useDocumentTransformationData({ documentId });
  const completeness = latestTransformation?.processing_metadata?.completeness;
  const [search, setSearch] = useState("");
  const [mappingOpen, setMappingOpen] = useState(false);
  const [sourceOpen, setSourceOpen] = useState(false);
  // Reject requires a reason (#7441) — same dialog as the single-client tab.
  const [rejectOpen, setRejectOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { hasCapability: canCascadeDelete } = useCascadeDeletePermission();
  const { canCreate, isLoading: permissionsLoading } = useResourcePermissions(ResourceName.DOCUMENTS);
  const { partner } = usePartner();
  // View-only once permissions resolve without documents:create (#7431 AC2);
  // a read-only partner (ADR-035) is its own cause with its own remedy.
  const isViewOnly = !permissionsLoading && !canCreate;
  const isPartnerReadOnly = !!partner?.readOnly;
  const redirect = useRedirect();
  // Name for the dialog subtitle only; the show page has this record cached.
  const { data: panelDoc } = useGetOne<DocumentRead>(
    ResourceName.DOCUMENTS,
    { id: documentId },
    { enabled: !!documentId }
  );

  // Reviewer-excluded reimbursement rows, tracked by extracted_record_index
  // (#5592). The index IS the 0-based position the worker excludes on, so the
  // set feeds straight into the accept payload with no remapping.
  const [excludedIndices, setExcludedIndices] = useState<Set<number>>(new Set());
  const handleExclude = useCallback((indices: number[]) => {
    setExcludedIndices((prev) => {
      const next = new Set(prev);
      indices.forEach((i) => next.add(i));
      return next;
    });
  }, []);
  const handleRestore = useCallback((indices: number[]) => {
    setExcludedIndices((prev) => {
      const next = new Set(prev);
      indices.forEach((i) => next.delete(i));
      return next;
    });
  }, []);
  // Flat, 0-based `extracted_record_index` set for the accept payload. This aligns
  // with the worker's `excludedSet[i]` (which filters `for i, rec := range
  // transformation.ExtractedRecords`) ONLY because the accept targets a single
  // transformation: mgmt-api's `sendDocumentApprovedMessage` passes one
  // `transformationID`, so the worker takes the `req.TransformationID != ""` →
  // `transformations[:1]` branch and there is a single index space. Do NOT send
  // these indices on an accept without a `transformation_id` — the worker's other
  // branch loops every transformation and applies the same set to each one's local
  // `i`, which would silently exclude the wrong financial rows.
  const excludedRecordIndices = useMemo(() => Array.from(excludedIndices).sort((a, b) => a - b), [excludedIndices]);

  // Reject + Approve & Load reuse the single-client review hook. A BOB document
  // has no single client, so selectedClient is null and isClientMissing is false
  // — the accept handler is client-agnostic (the worker resolves each row's
  // client/policy on load). Excluded rows flow through as excluded_record_indices.
  const { handleApprove, handleReject, isApproving, isRejecting } = useReviewApproval({
    documentId,
    transformationId: latestTransformation?.id,
    selectedClient: null,
    isClientMissing: false,
    excludedRecordIndices,
    // Complete by SOURCE, not by luck of size. Every index this panel can
    // exclude comes from the projection — claimantRowIndices maps
    // `claimant.rows[].extracted_record_index` straight off it — so excluding
    // a client already covers every one of its claimants' rows across the
    // whole document. There is no record window here to be blind to, and this
    // panel never calls the records endpoint.
    //
    // Stating that explicitly is the fix for having omitted it: the previous
    // optional counts defaulted to "total unknown", so every BOB approval
    // with an exclusion tried to resolve linkage against a transformation
    // whose records it had never fetched, and was blocked (#6848).
    exclusionScope: { source: "whole-document-projection" },
  });

  // Hooks must run before the loading/error/empty early-returns below.
  const clientRows = useMemo<ClientEntry[]>(
    () =>
      (projection?.clients ?? []).map((group, i) => ({
        id: `${group.existing_client_id ?? group.client_name}-${i}`,
        group,
      })),
    [projection]
  );
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? clientRows.filter((c) => c.group.client_name.toLowerCase().includes(q)) : clientRows;
  }, [clientRows, search]);

  // Aggregate issues for the sidebar "Review Summary" card (one pass over the
  // already-loaded projection). Mirrors the single-client review's sidebar.
  // Reviewer-excluded rows are dispositioned, so they drop out of the issue
  // counts (#5592) — a client whose every row is excluded also stops counting as
  // a new client that will be created.
  const summary = useMemo(() => {
    let noClient = 0;
    let ambiguous = 0;
    let multiPolicy = 0;
    let unlinked = 0;
    let newClients = 0;
    for (const g of projection?.clients ?? []) {
      let groupHasIncluded = false;
      for (const cl of g.claimants) {
        for (const r of cl.rows) {
          if (excludedIndices.has(r.extracted_record_index)) continue;
          groupHasIncluded = true;
          if (r.resolution_status === "quarantine_no_client") noClient++;
          else if (r.resolution_status === "quarantine_ambiguous_client") ambiguous++;
          else if (r.resolution_status === "quarantine_multi_policy") multiPolicy++;
          else if (r.resolution_status === "resolved_unlinked") unlinked++;
        }
      }
      if (g.match_status === "new" && groupHasIncluded) newClients++;
    }
    // Claimant-less rows are excluded from client groups by the backend; their
    // count comes through the top-level field (not reviewer-excludable here).
    const noClaimant = projection?.no_claimant_rows ?? 0;
    return { noClient, ambiguous, multiPolicy, unlinked, newClients, noClaimant };
  }, [projection, excludedIndices]);

  // Per-row load disposition keyed by extracted_record_index, so reviewer
  // exclusions can be subtracted from the approve-gating counts (mirrors the
  // single-client review, where excluded records drop out of the gating tallies
  // while the descriptive sidebar stays a raw view of the extraction).
  const rowStatusByIndex = useMemo(() => {
    const m = new Map<number, "loadable" | "quarantine">();
    for (const g of projection?.clients ?? []) {
      for (const cl of g.claimants) {
        for (const r of cl.rows) {
          const loadable = r.resolution_status === "resolved" || r.resolution_status === "resolved_unlinked";
          m.set(r.extracted_record_index, loadable ? "loadable" : "quarantine");
        }
      }
    }
    return m;
  }, [projection]);

  // Client-row selection (level 1). Excluding a client cascades to every row it
  // owns; the row mutes without needing to expand (#5592).
  const {
    selectedIds: selectedClientIds,
    setSelectedIds: setSelectedClientIds,
    toolbar: clientToolbar,
  } = useExcludeSelection(filteredRows, clientIndicesOf, excludedIndices, handleExclude, handleRestore);

  const clientColumns: CompactTableColumn<ClientEntry>[] = [
    {
      key: "client",
      label: "Client",
      width: "1.5fr",
      render: (c) => <Typography variant="body2">{c.group.client_name || "(unresolved)"}</Typography>,
    },
    {
      key: "status",
      label: "Status",
      width: "160px",
      render: (c) => <ClientMatchChip status={c.group.match_status} />,
    },
    {
      key: "claimants",
      label: "Claimants",
      width: "90px",
      align: "center",
      render: (c) => (
        <Typography variant="body2">{formatCount(clientTotals(c.group, excludedIndices).claimants)}</Typography>
      ),
    },
    {
      key: "rows",
      label: "Rows",
      width: "1fr",
      render: (c) => <RowCountsCell group={c.group} excludedIndices={excludedIndices} />,
    },
    {
      key: "reimbursed",
      label: "Reimbursed",
      width: "120px",
      align: "right",
      render: (c) => (
        <Typography variant="body2">{formatUSD(clientTotals(c.group, excludedIndices).reimbursed)}</Typography>
      ),
    },
  ];

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" gap={1} py={3}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">
          Computing the multi-client forecast…
        </Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Could not load the multi-client review forecast: {error}</Alert>;
  }

  if (!projection || projection.clients.length === 0) {
    return (
      <Alert severity="info">
        No clients were resolved from this document yet. If processing just finished, refresh; otherwise the rows may
        need review.
      </Alert>
    );
  }

  const noClaimantRows = projection.no_claimant_rows ?? 0;
  const totalRows = projection.clients.reduce((sum, c) => sum + c.total_rows, 0) + noClaimantRows;
  // Split the reviewer's exclusions by disposition so they can be subtracted
  // from the gating counts below (an excluded row won't load, and no longer
  // needs review).
  let excludedLoadable = 0;
  let excludedNeedsReview = 0;
  for (const idx of excludedIndices) {
    const st = rowStatusByIndex.get(idx);
    if (st === "loadable") excludedLoadable++;
    else if (st === "quarantine") excludedNeedsReview++;
  }
  // Actionable review items only: ambiguous client / multiple policy terms / no
  // client name. Claimant-less separator rows are NOT counted — they just skip,
  // there's nothing for the reviewer to do, so they don't drive the toolbar pill
  // or the approve confirm dialog. Excluded rows are dispositioned, so they drop.
  const needsReview = Math.max(
    0,
    projection.clients.reduce((sum, c) => sum + c.quarantined_rows, 0) - excludedNeedsReview
  );
  // Rows that will actually load (resolved + unlinked, minus reviewer
  // exclusions). Approve is blocked when none would load — quarantine/unlinked
  // otherwise stay warnings (best-effort partial load, ADR-057).
  const loadableRows = Math.max(
    0,
    projection.clients.reduce((sum, c) => sum + c.resolved_rows + c.unlinked_rows, 0) - excludedLoadable
  );
  const nothingToLoad = loadableRows === 0;
  // Source preview renders the whole sheet (no virtualization), so disable it for
  // large files — the per-record row numbers are enough; download for the full data.
  const sourceTooLarge = totalRows > SOURCE_PREVIEW_MAX_ROWS;

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap">
        <Typography variant="body2" color="text.secondary">
          {formatCount(totalRows)} record{totalRows === 1 ? "" : "s"} extracted ·{" "}
          {formatCount(projection.clients.length)} client{projection.clients.length === 1 ? "" : "s"}
        </Typography>
        {excludedIndices.size > 0 && (
          <Typography variant="body2" color="text.disabled" sx={{ fontStyle: "italic" }}>
            {formatCount(excludedIndices.size)} excluded
          </Typography>
        )}
        {/* Completeness guardrail (#5767) — compact chip; detail in the sidebar card. */}
        <CompletenessChip completeness={completeness} />
        {needsReview > 0 && (
          <Chip
            size="small"
            label={`${formatCount(needsReview)} need review`}
            sx={{ ...baseChipSx, bgcolor: "error.main", color: "error.contrastText" }}
          />
        )}
        <Box sx={{ flex: 1 }} />
        {/* View source — opens the spreadsheet preview (lazy). Disabled for large
            files (the preview isn't virtualized); the per-record row numbers + the
            downloadable file cover those. */}
        <Tooltip
          title={
            sourceTooLarge
              ? `Source preview is unavailable for large files (${formatCount(totalRows)} rows). Use the row numbers on each record, or download the file.`
              : ""
          }
          disableHoverListener={!sourceTooLarge}
          arrow
        >
          <span>
            <MUIButton
              size={UIComponentSize.Small}
              variant={ButtonStyleType.Text}
              onClick={() => setSourceOpen(true)}
              disabled={sourceTooLarge}
              startIcon={<TableChartOutlined />}
              sx={{ whiteSpace: "nowrap" }}
            >
              View source
            </MUIButton>
          </span>
        </Tooltip>
        {/* Reuses the single-client review toolbar. No client gate (BOB resolves
            per row). Quarantined rows are warnings (confirm dialog); approve is
            blocked only when nothing would load.

            NO onReprocess HERE, DELIBERATELY (#6920, !4711 review
            [AEM-bob-surface-unwired]). #6920 added a Reprocess control to the
            two single-client header variants and to the document queue. This
            surface is the third ReviewActions call site and is left without it
            ON PURPOSE, because `onReprocess?` is optional and would otherwise
            omit it silently:

            - BOB rows DO get the queue row action (DocumentQueue keys on
              status only, with no multi-client condition), so a reviewer is
              not stuck.
            - The confirm's excludedCount has no meaning here — this panel holds
              no exclusion state and no as-of state, so a copied wiring would
              assert reviewer work that cannot exist on this screen. That is the
              exact class of wrong-copy bug !4711's review found twice.
            - No error copy on this surface names "reprocess": the errored-records
              banner is single-client only (#6944) and the projection-error copy
              is not rendered here. So there is no promise to keep, which is the
              argument #6920 was built on.
            - Per ADR-045's 2026-09-15 amendment §5, this panel has no
              record-count guard in EITHER tier (#7019), so it likely warrants
              its own treatment rather than a copy of the single-client wiring.

            Tracked as a child of #6920. */}
        <ReviewActions
          onReviewMapping={() => setMappingOpen(true)}
          onApprove={handleApprove}
          onReject={() => setRejectOpen(true)}
          onDeleteDocument={canCascadeDelete ? () => setDeleteOpen(true) : undefined}
          isApproving={isApproving}
          isRejecting={isRejecting}
          readOnly={isViewOnly}
          partnerReadOnly={isPartnerReadOnly}
          nothingToLoad={nothingToLoad}
          isChecking={isLoading}
          isReadyForApproval={!nothingToLoad}
          hasTransformation
          isClientMissing={false}
          errorCount={0}
          warningCount={needsReview}
          mappingBlockingCount={0}
          mappingAdvisoryCount={0}
        />
      </Stack>

      <RejectDocumentDialog
        open={rejectOpen}
        documentName={panelDoc?.name}
        isRejecting={isRejecting}
        onCancel={() => setRejectOpen(false)}
        onConfirm={async (input) => {
          if (await handleReject(input)) setRejectOpen(false);
        }}
      />
      {documentId && (
        <DeleteImpactDialog
          open={deleteOpen}
          documentId={documentId}
          documentName={panelDoc?.name}
          onClose={() => setDeleteOpen(false)}
          onDeleted={() => redirect("list", ResourceName.DOCUMENTS)}
          onCascadeStarted={() => redirect("list", ResourceName.DOCUMENTS)}
        />
      )}

      {/* What the extractor did to the rows and did not report (#6921).
          #6921's stated defect — "a reviewer approving a document had no way
          to know either happened" — was true verbatim on this surface too:
          is_multi_client documents route here instead of DocumentReviewTab,
          and the notice had exactly one call site (!4682 review).

          The ERRORED-RECORDS BANNER is deliberately NOT wired here, and that
          is a scoped decision rather than an omission. Its two affordances do
          not exist on this surface: "Show in <tab>" navigates the schema tabs,
          which this panel does not have, and the per-group exclude routes
          through a confirm dialog built around ErroredRecordGroup. Giving both
          a BOB-shaped design is real design work, and inventing it inside a
          review-response pass is how the first version of this banner ended up
          being rejected as clunky. Filed rather than guessed. */}
      <RowDecisionsNotice decisions={selectRowDecisions(latestTransformation)} />

      {nothingToLoad && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Nothing to load — every row is excluded, needs review, or has no claimant. Restore a row or resolve the issues
          in the Review Summary, then re-process to load.
        </Alert>
      )}

      {documentId && mappingOpen && (
        <BOBFieldMappingDialog documentId={documentId} onClose={() => setMappingOpen(false)} />
      )}

      {documentId && sourceOpen && <BOBSourcePreview documentId={documentId} onClose={() => setSourceOpen(false)} />}

      {projection.warnings?.map((w, i) => (
        <Alert key={i} severity="warning" sx={{ mb: 1 }}>
          {w.message}
        </Alert>
      ))}

      {/* Two-column layout: main (search + client table) + issues sidebar,
          mirroring the single-client review's grid + Mapping Review sidebar.
          Sidebar only renders when there are issues (else full-width). */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns:
            hasSidebarIssues(summary) || hasCompletenessSignal(completeness)
              ? "minmax(0, 1fr) 340px"
              : "minmax(0, 1fr)",
          gap: 2.5,
          alignItems: "flex-start",
          maxWidth: "100%",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <TextField
            placeholder="Search clients..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 1.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <CompactTable<ClientEntry>
            data={filteredRows}
            columns={clientColumns}
            rowsPerPage={25}
            emptyMessage={search ? `No clients match "${search}".` : "No clients resolved."}
            selectable
            selectedIds={selectedClientIds}
            onSelectionChange={setSelectedClientIds}
            selectionToolbar={clientToolbar}
            getRowSx={(c) => (allExcluded(clientRowIndices(c.group), excludedIndices) ? excludedRowSx : {})}
            expandRow={(c) => (
              <Box sx={{ pl: 2, py: 1 }}>
                <ClientClaimantsTable
                  group={c.group}
                  excludedIndices={excludedIndices}
                  onExclude={handleExclude}
                  onRestore={handleRestore}
                />
              </Box>
            )}
          />
        </Box>

        {(hasSidebarIssues(summary) || hasCompletenessSignal(completeness)) && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <CompletenessCard completeness={completeness} />
            {hasSidebarIssues(summary) && <BOBReviewSidebar summary={summary} />}
          </Box>
        )}
      </Box>
    </Box>
  );
};
